import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  InlineStack,
  Layout,
  Page,
  Spinner,
  Text,
} from "@shopify/polaris";
import type { WheelConfigV1 } from "../types/wheel";
import {
  DEFAULT_WHEEL_PREVIEW_COLORS,
  isWheelCatalogTemplateId,
  type WheelTemplatePreviewColors,
} from "../types/wheel";
import { WHEEL_CATALOG_TEMPLATES } from "../components/games/wheelCatalogTemplates";
import {
  WheelTemplateConfigForm,
  normalizeTemplateWheelConfig,
} from "../components/games/WheelTemplateConfigForm";
import { WheelGamePreview } from "../components/games/previews/WheelGamePreview";
import {
  fetchWheelTemplateDefaults,
  updateWheelTemplateDefaults,
} from "../api/wheel-templates";

export default function WheelTemplateEditPage() {
  const { templateId: rawId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const templateId = rawId && isWheelCatalogTemplateId(rawId) ? rawId : null;

  const backToGames = useCallback(() => {
    navigate({ pathname: "/games", search: location.search });
  }, [navigate, location.search]);

  const meta = useMemo(
    () => WHEEL_CATALOG_TEMPLATES.find((t) => t.id === templateId),
    [templateId]
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedBanner, setSavedBanner] = useState(false);

  const [wheelConfig, setWheelConfig] = useState<WheelConfigV1 | null>(null);
  const [previewColors, setPreviewColors] = useState<WheelTemplatePreviewColors>(
    DEFAULT_WHEEL_PREVIEW_COLORS
  );

  useEffect(() => {
    if (!templateId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetchWheelTemplateDefaults(templateId)
      .then((res) => {
        if (cancelled) return;
        setWheelConfig(normalizeTemplateWheelConfig(res.wheelConfig, templateId));
        setPreviewColors({
          ...DEFAULT_WHEEL_PREVIEW_COLORS,
          ...res.previewColors,
          segmentHexes: res.previewColors.segmentHexes,
        });
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setLoadError(e instanceof Error ? e.message : "Failed to load template defaults");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  const handleSave = useCallback(async () => {
    if (!templateId || !wheelConfig) return;
    setSaving(true);
    setSaveError(null);
    setSavedBanner(false);
    try {
      await updateWheelTemplateDefaults(templateId, { wheelConfig, previewColors });
      setSavedBanner(true);
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : "Failed to save template defaults");
    } finally {
      setSaving(false);
    }
  }, [templateId, wheelConfig, previewColors]);

  const updateSegmentColor = (index: number, hex: string) => {
    setPreviewColors((prev) => {
      const next = [...prev.segmentHexes] as [string, string, string, string, string, string];
      next[index] = hex;
      return { ...prev, segmentHexes: next };
    });
  };

  if (!templateId || !meta) {
    return (
      <Page title="Wheel template" backAction={{ content: "Games", onAction: backToGames }}>
        <Card>
          <Box padding="400">
            <Text as="p">Unknown wheel template.</Text>
            <Box paddingBlockStart="300">
              <Button onClick={backToGames}>Back to Games</Button>
            </Box>
          </Box>
        </Card>
      </Page>
    );
  }

  if (loading) {
    return (
      <Page title={meta.title} backAction={{ content: "Games", onAction: backToGames }}>
        <Card>
          <Box padding="800">
            <BlockStack align="center" gap="400">
              <Spinner size="large" />
              <Text as="p" variant="bodyMd" tone="subdued">
                Loading template defaults…
              </Text>
            </BlockStack>
          </Box>
        </Card>
      </Page>
    );
  }

  if (loadError || !wheelConfig) {
    return (
      <Page title={meta.title} backAction={{ content: "Games", onAction: backToGames }}>
        <Banner tone="critical" onDismiss={() => setLoadError(null)}>
          {loadError ?? "Could not load template defaults."}
        </Banner>
        <Box paddingBlockStart="400">
          <Button onClick={backToGames}>Back to Games</Button>
        </Box>
      </Page>
    );
  }

  return (
    <Page
      title={meta.title}
      subtitle="Template defaults for your shop. New campaigns that use this template start from these settings; each campaign variant can change them later."
      backAction={{ content: "Games", onAction: backToGames }}
      primaryAction={{
        content: "Save template defaults",
        onAction: handleSave,
        loading: saving,
        disabled: saving,
      }}
    >
      <Layout>
        <Layout.Section>
          {saveError && (
            <Banner tone="critical" onDismiss={() => setSaveError(null)}>
              {saveError}
            </Banner>
          )}
          {savedBanner && (
            <Banner tone="success" onDismiss={() => setSavedBanner(false)}>
              Saved. Use in Campaign will use these defaults when you add a wheel variant.
            </Banner>
          )}
        </Layout.Section>

        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text as="p" variant="bodyMd" tone="subdued">
                  {meta.description}
                </Text>
                <Text variant="headingSm" as="h3">
                  Catalog preview
                </Text>
                <Box maxWidth="220px">
                  <WheelGamePreview
                    segmentFills={previewColors.segmentHexes}
                    pointerFill={previewColors.pointerHex}
                    rimStroke={previewColors.rimStrokeHex}
                    outerRingFill={previewColors.outerRingHex}
                  />
                </Box>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text variant="headingSm" as="h3">
                  Catalog preview colors
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Affects the Games catalog and this page only. The live storefront wheel uses your
                  campaign variant settings.
                </Text>
                <InlineStack gap="400" wrap blockAlign="center">
                  <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <Text as="span" variant="bodySm">
                      Pointer
                    </Text>
                    <input
                      type="color"
                      value={previewColors.pointerHex}
                      onChange={(e) =>
                        setPreviewColors((p) => ({ ...p, pointerHex: e.target.value }))
                      }
                      aria-label="Pointer color"
                    />
                  </label>
                  <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <Text as="span" variant="bodySm">
                      Rim stroke
                    </Text>
                    <input
                      type="color"
                      value={previewColors.rimStrokeHex}
                      onChange={(e) =>
                        setPreviewColors((p) => ({ ...p, rimStrokeHex: e.target.value }))
                      }
                      aria-label="Rim stroke color"
                    />
                  </label>
                  <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <Text as="span" variant="bodySm">
                      Outer ring
                    </Text>
                    <input
                      type="color"
                      value={previewColors.outerRingHex}
                      onChange={(e) =>
                        setPreviewColors((p) => ({ ...p, outerRingHex: e.target.value }))
                      }
                      aria-label="Outer ring color"
                    />
                  </label>
                </InlineStack>
                <Text variant="headingSm" as="h4">
                  Segment colors
                </Text>
                <InlineStack gap="300" wrap blockAlign="center">
                  {previewColors.segmentHexes.map((hex, i) => (
                    <label
                      key={i}
                      style={{ display: "flex", flexDirection: "column", gap: 4 }}
                    >
                      <Text as="span" variant="bodySm">
                        Segment {i + 1}
                      </Text>
                      <input
                        type="color"
                        value={hex}
                        onChange={(e) => updateSegmentColor(i, e.target.value)}
                        aria-label={`Segment ${i + 1} color`}
                      />
                    </label>
                  ))}
                </InlineStack>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <Box padding="400">
              <WheelTemplateConfigForm
                templateId={templateId}
                value={wheelConfig}
                onChange={setWheelConfig}
              />
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
